import { Button } from '@/components/ui/button';
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { 
  Bars3Icon, 
  XMarkIcon, 
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import abogaliaLogo from '../assets/abogalia.webp';
import abogaliaLogoDark from '../assets/abogalia-dark.webp';
import { LawyerHeaderMenu } from './LawyerHeaderMenu';
import { useTheme } from 'next-themes';
import { useEffect } from 'react';

const navigationItems = [
  //{ label: 'Cómo funciona', href: '#how_works', id: "item-1" },
  //{ label: 'Características', href: '#features', id: "item-2" },
  { label: 'Casos publicados', href: 'marketplace', id: "item-3" },
  { label: 'Directorio de Abogados', href: 'lawyer/directory', id: "item-4" },
  { label: 'Planes para Abogados', href: 'pricing', id: "item-5" },
  { label: 'LegalBot', href: 'legalbot', id: "item-6" },
];

export function HomeHeader() {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const logo = (mounted && theme === 'dark') ? abogaliaLogoDark : abogaliaLogo;

  const handleLoginClick = () => {
    navigate('/user/login');
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b bg-white/80 backdrop-blur-md supports-[backdrop-filter]:bg-white/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center gap-2">
              <button
                onClick={() => window.location.href = `/`}
                className="group flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:bg-violet-50 hover:scale-105"
              >
                <img 
                  src={logo}
                  alt="Logo" 
                  className="h-8 w-auto transition-transform duration-200 group-hover:scale-110"
                />
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4 relative">
              <Button
                variant="ghost"
                onClick={() => navigate('/lawyer/directory/ranking')}
                className="text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100"
              >
                Mejores abogados
              </Button>
              <LawyerHeaderMenu navigationItems={navigationItems} />
              <Button className='bg-violet-600 hover:bg-violet-700 text-white' onClick={handleLoginClick}>
                Iniciar Sesión
              </Button>
            </div>

            {/* Mobile Navigation */}
            <div className="md:hidden h-full flex items-center">
              <button
                onClick={() => setIsOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-violet-700 hover:bg-violet-50 transition-colors duration-200"
              >
                <span className="sr-only">Abrir menú</span>
                <Bars3Icon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>

    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setIsOpen(false)}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-start justify-end">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-x-full scale-95"
              enterTo="opacity-100 translate-x-0 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-x-0 scale-100"
              leaveTo="opacity-0 translate-x-full scale-95"
            >
              <DialogPanel className="w-80 h-auto max-h-[90vh] transform overflow-hidden bg-white/95 backdrop-blur-xl p-4 shadow-2xl transition-all rounded-2xl border border-white/20 m-4">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle className="text-lg font-semibold text-gray-800">
                    Menú
                  </DialogTitle>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="rounded-full p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all duration-200"
                  >
                    <span className="sr-only">Cerrar</span>
                    <XMarkIcon className="h-5 w-5 flex-shrink-0" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Link destacado: Mejores abogados (para todos, no solo abogados) */}
                  <button
                    onClick={() => {
                      navigate('/lawyer/directory/ranking');
                      setIsOpen(false);
                    }}
                    className="w-full px-3 py-2.5 text-left rounded-lg text-sm font-medium text-gray-700 hover:text-violet-700 hover:bg-violet-50 transition-all duration-200"
                  >
                    Mejores abogados
                  </button>

                  {/* Separador */}
                  <div className="border-t border-gray-200/50"></div>

                  {/* Sección Para Abogados */}
                  <div>
                    <div className="flex items-center text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                      <div className="w-2 h-2 bg-violet-400 rounded-full mr-2"></div>
                      Para Abogados
                    </div>
                    <div className="space-y-1">
                      {navigationItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            if (item.href === 'legalbot') {
                              window.open(`/${item.href}`, '_blank', 'noopener,noreferrer');
                            } else {
                              navigate(`/${item.href}`);
                            }
                            setIsOpen(false);
                          }}
                          className="w-full px-3 py-2.5 text-left rounded-lg text-sm text-gray-700 hover:text-violet-700 hover:bg-violet-50 transition-all duration-200"
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Separador */}
                  <div className="border-t border-gray-200/50"></div>

                  {/* Botón destacado de Iniciar Sesión */}
                  <div>
                    <button
                      onClick={() => {
                        handleLoginClick();
                        setIsOpen(false);
                      }}
                      className="w-full px-4 py-3 text-center rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]"
                    >
                      Iniciar Sesión
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
    </>
  );
}